import { AnchorMark, ArchMark, ThermalMark, LeisureMark } from "@/components/marks";
import { LexiconTile, ArticleCard } from "@/components/shared";
import { images } from "@/lib/constants";
import { useLocation } from "wouter";
import { ArrowRight } from "lucide-react";

export function HomePage() {
  const [, setLocation] = useLocation();

  return (
    <>
      {/* Hero */}
      <section className="pt-32 md:pt-48 pb-16 md:pb-24 px-6 md:px-12 max-w-7xl mx-auto relative overflow-hidden">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">
          <div className="w-full lg:w-1/2 relative z-10 flex flex-col items-center text-center lg:items-start lg:text-left mt-8 lg:mt-0">
            <ArchMark className="w-16 h-16 md:w-20 md:h-20 mb-8 text-[#C06B5D]" />
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] md:leading-[0.9] uppercase mb-6 text-[#293129] w-full">
              Playful<br /><span className="italic text-[#1D4C74]">Sophistication.</span><br />Sacred Earth.
            </h1>
            <p className="text-xs md:text-sm tracking-[0.2em] leading-loose uppercase max-w-md font-medium opacity-80 lg:border-l-2 lg:border-[#C06B5D] lg:pl-6 mx-auto lg:mx-0">
              A 20-room leisure club and thermal bathhouse built upon the legacy of an eccentric citizen scientist.
            </p>
          </div>
          <div className="w-full lg:w-1/2 flex justify-center lg:justify-end mt-8 lg:mt-0">
            <div className="relative w-full max-w-sm md:max-w-md group cursor-pointer" onClick={() => setLocation("/gallery")}>
              <div className="absolute inset-0 bg-[#8F9B82] arch-crop translate-x-4 translate-y-4 md:translate-x-6 md:translate-y-6 transition-transform group-hover:translate-x-8 group-hover:translate-y-8 duration-500" />
              <img src={images.hero} alt="Adobe Architecture" className="relative w-full h-[50vh] md:h-[70vh] object-cover arch-crop shadow-xl text-transparent grayscale-[10%]" />
            </div>
          </div>
        </div>
      </section>

      {/* Legacy + Lexicon */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto border-b border-[#293129]/20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32">
          <div className="order-2 lg:order-1 grid grid-cols-2 gap-4 h-full">
            <div className="h-48 md:h-64 bg-[#C06B5D] arch-crop flex items-center justify-center p-6 text-center">
              <p className="font-serif italic text-2xl md:text-3xl text-[#ECE9E0]">San Jacinto<br />Views.</p>
            </div>
            <div className="h-48 md:h-64 bg-transparent border border-[#293129] arch-crop flex items-center justify-center relative overflow-hidden">
              <img src={images.agave} alt="Desert Flora" className="absolute inset-0 w-full h-full object-cover opacity-80 mix-blend-multiply text-transparent" />
            </div>
            <div className="h-48 md:h-64 col-span-2 bg-[#8F9B82] p-8 flex flex-col justify-end cursor-pointer group hover:bg-[#1D4C74] transition-colors" onClick={() => setLocation("/gallery")}>
              <p className="text-[#ECE9E0] tracking-[0.2em] text-xs uppercase font-bold text-right flex items-center justify-end space-x-2 group-hover:translate-x-2 transition-transform">
                <span>Explore the Compound Archives</span><ArrowRight size={16} />
              </p>
            </div>
          </div>
          <div className="order-1 lg:order-2 lg:pl-12">
            <h2 className="font-serif text-5xl md:text-6xl mb-8 uppercase">The Legacy of the Host</h2>
            <p className="tracking-widest uppercase text-xs leading-loose opacity-80 mb-8">
              Miracle Hill centers around an adobe residence that once belonged to an eccentric host—a citizen scientist obsessed with the convergence of natural forces at this precise location.
            </p>
            <p className="tracking-widest uppercase text-xs leading-loose opacity-80 mb-10">
              His legacy lives on in every design choice: San Jacinto view saunas playing soothing binaural beats, pools terraced to follow the earth's temperature gradients, and a pantry stocked for endless gathering.
            </p>
            <button onClick={() => setLocation("/gallery")} className="flex items-center space-x-3 text-xs tracking-[0.2em] font-bold uppercase hover:text-[#C06B5D] transition-colors">
              <span>View the Gallery</span><ArrowRight size={16} />
            </button>
          </div>
        </div>

        <div className="pt-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl uppercase mb-4">The Lexicon</h2>
            <p className="tracking-widest uppercase text-xs opacity-60">A fluid brand ecosystem.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <LexiconTile mark={<AnchorMark className="w-full" />} label="The Anchor" bgColor="#1D4C74" href="/brand" />
            <LexiconTile mark={<ArchMark className="w-16 h-16" />} label="The Architect" bgColor="#C06B5D" shape="arch" href="/brand" />
            <LexiconTile mark={<ThermalMark className="w-20 h-20" />} label="The Scientist" bgColor="transparent" textColor="#293129" shape="circle" href="/brand" />
            <LexiconTile mark={<LeisureMark className="w-20 h-20" />} label="The Leisure Seal" bgColor="#8F9B82" href="/brand" />
          </div>
          <div className="text-center mt-8">
            <button onClick={() => setLocation("/brand")} className="text-[10px] tracking-[0.2em] font-bold uppercase border-b border-[#293129] pb-1 hover:text-[#C06B5D] hover:border-[#C06B5D] transition-colors">
              Read Full Brand Guidelines &rarr;
            </button>
          </div>
        </div>
      </section>

      {/* Almanac */}
      <section className="py-24 px-6 md:px-12 bg-[#ECE9E0]">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-16 border-b border-[#293129] pb-6">
            <h2 className="font-serif text-5xl md:text-6xl uppercase">The Almanac</h2>
            <p className="hidden md:block tracking-widest uppercase text-xs font-bold opacity-60">Vol. I — Research & Leisure</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <ArticleCard
              image={images.pool}
              number="01"
              category="GEOLOGY"
              categoryColor="#1D4C74"
              title="Thermal Mapping the Desert Fault Line"
              href="/article"
            />
            <ArticleCard
              image={images.lounge}
              number="02"
              category="ARCHITECTURE"
              categoryColor="#8F9B82"
              title="The Return of Desert Leisure"
              href="/article"
            />
            <ArticleCard
              markFallback={<ThermalMark className="w-32 h-32 text-[#ECE9E0] opacity-30 group-hover:scale-110 group-hover:opacity-100 transition-all duration-700" />}
              number="03"
              category="METAPHYSICS"
              categoryColor="#293129"
              title="Mapping Invisible Vortex Currents"
              href="/article"
            />
          </div>
        </div>
      </section>
    </>
  );
}
